import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Header from './components/Header';
import Filters from './components/Filters';
import Row from './components/Row';
import TableHeaderSection from './components/TableHeaderSection';
import TableBox from '../../../components/TableBox';
import Modal from '../../../components/Modal';
import Button from "../../../components/Button";
import { HeadCellType, TableContextProvider } from '../../../interfaces/common';
import { ListFilters } from '../../../interfaces/transaction.interface';
import { buildHeadCells, getStartEndOfMonth } from '../../../utils/methods';
import { GENERIC_LOOKUP_KEYS, VALIDATION_FIELD_MAPPING, HEAD_CELL_SORT_MAPPING, TRANSACTION_STATUS } from '../../../utils/appConstant';
import useSorting from '../../../hooks/useSorting';
import usePagination from '../../../hooks/usePagination';
import useUrlParams from '../../../hooks/useUrlParams';
import useFetch from '../../../hooks/useFetch';
import TransactionService from '../../../services/transaction.service';
import CommonService from '../../../services/common.service';
import PropertyService from '../../../services/property.service';
import UserService from '../../../services/user.service';
import VendorService from '../../../services/vendor.service';
import { SortOrder, Modules } from '../../../enums';
import { usePersistentFilters } from '../../../hooks/usePersistentFilters';
import { toast } from "react-toastify";
import Toast from "../../../components/Toast";
import { APP_SUCCESS_MESSAGES } from "../../../utils/appConstant";

const List: React.FC = () => {
  const { t } = useTranslation();

  const defaultFilters: ListFilters = {
    status: [TRANSACTION_STATUS.unbilled, TRANSACTION_STATUS.invoiced],
    searchText: '',
    propertyId: '',
    transactionType: '',
    debitCredit: '',
    assignedTo: '',
    reviewedBy: '',
    vendorId: '',
    chargeable: '',
    expenseCategory: '',
    adjustAgainstClaim: '',
    claimSettled: '',
    ...getStartEndOfMonth(),
  };

  const { initialFilters, saveFilters, resetFilters } = usePersistentFilters<ListFilters>('filters_transactions', defaultFilters);

  const [filters, setFilters] = useState<ListFilters>(initialFilters);

  const [dropdownSearchData, setDropdownSearchData] = useState<any>({
    propertyId: '',
    transactionType: '',
    assignedTo: '',
    status: '',
    debitCredit: '',
    reviewedBy: '',
    vendorId: '',
    chargeable: '',
    expenseCategory: '',
    adjustAgainstClaim: '',
    claimSettled: '',
  });
  const [kpis, setKpis] = useState<any>({});
  const [removalId, setRemovalId] = useState<string>('');
  const [removalHasDeleteAccess, setRemovalHasDeleteAccess] = useState<boolean>(false);
  const [counter, setCounter] = useState<number>(0);

  const headCells: HeadCellType[] = buildHeadCells([
    VALIDATION_FIELD_MAPPING.transactionDate,
    VALIDATION_FIELD_MAPPING.propertyName,
    VALIDATION_FIELD_MAPPING.expenseCategory,
    VALIDATION_FIELD_MAPPING.transactionType,
    VALIDATION_FIELD_MAPPING.debitCredit,
    VALIDATION_FIELD_MAPPING.amount,
    VALIDATION_FIELD_MAPPING.totalAmount,
    VALIDATION_FIELD_MAPPING.status,
    VALIDATION_FIELD_MAPPING.invoiceNumber,
    VALIDATION_FIELD_MAPPING.adjustAgainstClaim,
    VALIDATION_FIELD_MAPPING.actions,
  ], 'transactionList');

  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const {
    order, orderBy, setOrder, setOrderBy
  } = useSorting({ defaultOrder: (searchParams.get('order') as SortOrder) || SortOrder.asc, defaultOrderBy: searchParams.get('orderBy') || VALIDATION_FIELD_MAPPING.transactionDate });

  const {
    page, perPage, setPage, setPerPage
  } = usePagination({ defaultPage: Number(searchParams.get('page')) || 1 });

  useUrlParams({
    type: Modules.transactions, uri: Modules.transactions, perPage, page, order, orderBy,
  });

  const { total, data: transactions, loading } = useFetch({
    service: TransactionService,
    func: 'list',
    payload: {
      page,
      perPage,
      orderBy: (HEAD_CELL_SORT_MAPPING.transactionList as any)[orderBy] || orderBy,
      order,
      ...filters,
    },
    dependency: [order, orderBy, page, perPage, filters.searchText, filters.propertyId, filters.transactionType, filters.debitCredit, filters.assignedTo, filters.reviewedBy, filters.vendorId, filters.status, filters.startDate, filters.endDate, filters.chargeable, filters.expenseCategory, filters.adjustAgainstClaim, filters.claimSettled, counter],
    onSuccess: (response: any) => {
      setKpis({
        unbilledCount: response.total_unbilled_count ?? 0,
        invoicedCount: response.total_invoiced_count ?? 0,
        settledCount: response.total_settled_count ?? 0,
        totalDebitsAmount: response.total_debits_amount ?? 0,
        totalCreditsAmount: response.total_credit_amount ?? 0,
      });
    },
  });

  const { data: allStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.transactionStatus, searchText: '' },
    dependency: []
  });

  const { data: status } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.transactionStatus, searchText: dropdownSearchData.status },
    dependency: [dropdownSearchData.status]
  });

  const { data: allProperties } = useFetch({
    service: PropertyService,
    func: 'lookup',
    payload: { searchText: '', is_active: '1' },
    dependency: [],
  });

  const { data: properties } = useFetch({
    service: PropertyService,
    func: 'lookup',
    payload: { searchText: dropdownSearchData.propertyId, is_active: '1' },
    dependency: [dropdownSearchData.propertyId],
  });

  const { data: allTransactionTypes } = useFetch({
    service: TransactionService,
    func: 'transactionTypeslookup',
    payload: { searchText: '' },
    dependency: []
  });

  const { data: transactionTypes } = useFetch({
    service: TransactionService,
    func: 'transactionTypeslookup',
    payload: { searchText: dropdownSearchData.transactionType },
    dependency: [dropdownSearchData.transactionType]
  });

  const { data: allCrdrOptions } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.crdr, searchText: '' },
    dependency: []
  });

  const { data: crdrOptions } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.crdr, searchText: dropdownSearchData.debitCredit },
    dependency: [dropdownSearchData.debitCredit]
  });

  const { data: allManagers } = useFetch({
    service: UserService,
    func: 'managersLookup',
    payload: { searchText: '', is_active: '1' },
    dependency: []
  });

  const { data: managers } = useFetch({
    service: UserService,
    func: 'managersLookup',
    payload: { searchText: dropdownSearchData.assignedTo, is_active: '1' },
    dependency: [dropdownSearchData.assignedTo]
  });

  const { data: allReviewedByOptions } = useFetch({
    service: UserService,
    func: 'lookup',
    payload: { searchText: '', is_active: '1' },
    dependency: [],
  });

  const { data: reviewedByOptions } = useFetch({
    service: UserService,
    func: 'lookup',
    payload: { searchText: dropdownSearchData.reviewedBy, is_active: '1' },
    dependency: [dropdownSearchData.reviewedBy],
  });

  const { data: allVendors } = useFetch({
    service: VendorService,
    func: 'lookup',
    payload: { searchText: '', is_active: '1' },
    dependency: [],
  });

  const { data: vendors } = useFetch({
    service: VendorService,
    func: 'lookup',
    payload: { searchText: dropdownSearchData.vendorId, is_active: '1' },
    dependency: [dropdownSearchData.vendorId],
  });

  const { data: allChargeableOptions } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.chargeableFilter, searchText: '' },
    dependency: [],
  });

  const { data: chargeableOptions } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.chargeableFilter, searchText: dropdownSearchData.chargeable },
    dependency: [dropdownSearchData.chargeable],
  });

  const { data: allTransactionCategoryOptions } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.transactionCategoryFilter, searchText: '' },
    dependency: [],
  });

  const { data: transactionCategoryOptions } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.transactionCategoryFilter, searchText: dropdownSearchData.expenseCategory },
    dependency: [dropdownSearchData.expenseCategory],
  });

  const { data: allAdjustAgainstClaimOptions } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.adjustAgainstClaimFilter, searchText: '' },
    dependency: [],
  });

  const { data: adjustAgainstClaimOptions } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.adjustAgainstClaimFilter, searchText: dropdownSearchData.adjustAgainstClaim },
    dependency: [dropdownSearchData.adjustAgainstClaim],
  });

  const { data: allClaimSettledOptions } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.claimSettledFilter, searchText: '' },
    dependency: [],
  });

  const { data: claimSettledOptions } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.claimSettledFilter, searchText: dropdownSearchData.claimSettled },
    dependency: [dropdownSearchData.claimSettled],
  });

  const handleApplyFilters = useCallback((newFilters: ListFilters) => {
    setPage(1);
    setFilters(newFilters);
    saveFilters(newFilters);
  }, [saveFilters, setPage]);

  const handleResetFilters = useCallback(() => {
    setPage(1);
    setFilters(defaultFilters);
    resetFilters();
  }, [resetFilters, setPage]);

  const handleDropdownSearchInputChange = useCallback(
    (event: any, name: string) => {
      setDropdownSearchData((prev: any) => {
        return {
          ...prev,
          [name]: event.target.value,
        };
      });
    },
    [setDropdownSearchData]
  );

  const handleRemoveClick = (id: string, hasDeleteAccess: boolean) => {
    setRemovalHasDeleteAccess(hasDeleteAccess);
    setRemovalId(id);
  }

  const handleRemoveTransaction = useCallback(async (id: string) => {
    try {
      const response: any = await TransactionService.deleteTransaction(id);
      if (response?.success !== false) {
        setCounter((prev) => prev + 1);
        toast.success(<Toast message={removalHasDeleteAccess ? APP_SUCCESS_MESSAGES.transactionDeleted : APP_SUCCESS_MESSAGES.transactionDeleteRequestSent} />, { icon: false });
      }
      setRemovalId('');
    } catch (e) {
      setRemovalId('');
    }
  }, [removalHasDeleteAccess]);

  const modalAction = removalHasDeleteAccess ? (
    <div className='d-flex gap-3'>
      <Button
        className="btn btn-outline-secondary"
        text={t("transactions.list.pageTexts.table.confirmModal.cancelBtn")}
        onClick={e => setRemovalId('')}
      />
      <Button
        className="btn btn-danger"
        text={t("transactions.list.pageTexts.table.confirmModal.deleteBtn")}
        onClick={e => handleRemoveTransaction(removalId)}
      />
    </div>
  ) : (
    <div className='d-flex gap-3'>
      <Button
        className="btn btn-outline-secondary"
        text={t("transactions.list.pageTexts.table.confirmModal.cancelBtn")}
        onClick={e => setRemovalId('')}
      />
      <Button
        className="btn btn-primary"
        text={t("transactions.list.pageTexts.table.confirmModal.sendRequestBtn")}
        onClick={e => handleRemoveTransaction(removalId)}
      />
    </div>
  )

  const headerSection = <TableHeaderSection total={total} kpis={kpis} />

  const values: TableContextProvider = useMemo(() => {
    return {
      rowProps: { handleRemoveClick },
      headerSection, row: Row, data: transactions || [], pagination: true, headCells, order, orderBy, page, perPage, total, loading, noDataFoundMessage: t('transactions.list.pageTexts.table.noDataFoundMessage'), setPage, setPerPage, setOrder, setOrderBy,
    };
  }, [headCells, order, orderBy, page, perPage, total, loading, transactions, setPage, setPerPage, setOrder, setOrderBy]);

  return (
    <>
      <Header />

      <Filters
        status={status}
        allStatus={allStatus}
        properties={properties}
        allProperties={allProperties}
        transactionTypes={transactionTypes}
        allTransactionTypes={allTransactionTypes}
        crdrOptions={crdrOptions}
        allCrdrOptions={allCrdrOptions}
        managers={managers}
        allManagers={allManagers}
        reviewedByOptions={reviewedByOptions}
        allReviewedByOptions={allReviewedByOptions}
        vendors={vendors}
        allVendors={allVendors}
        chargeableOptions={chargeableOptions}
        allChargeableOptions={allChargeableOptions}
        transactionCategoryOptions={transactionCategoryOptions}
        allTransactionCategoryOptions={allTransactionCategoryOptions}
        adjustAgainstClaimOptions={adjustAgainstClaimOptions}
        allAdjustAgainstClaimOptions={allAdjustAgainstClaimOptions}
        claimSettledOptions={claimSettledOptions}
        allClaimSettledOptions={allClaimSettledOptions}
        dropdownSearchData={dropdownSearchData}
        onDropdownSearchChange={handleDropdownSearchInputChange}
        initialFilters={initialFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />

      <TableBox tableValues={values} />

      <Modal
        open={removalId ? true : false}
        onClose={() => setRemovalId('')}
        title={t("transactions.list.pageTexts.table.confirmModal.title")}
        content={
          removalHasDeleteAccess
            ? t("transactions.list.pageTexts.table.confirmModal.content")
            : t("transactions.list.pageTexts.table.confirmModal.requestContent")
        }
        action={modalAction}
        contentClassName="text-center"
        maxWidth="sm"
      />
    </>
  );
};

export default List;
